export interface PerkQuoteContext {
  selectedVariantKey?: string | null;
  selectedTermMonths?: number | null;
  monthlyRecurringCents?: number | null;
  serviceConfiguration?: Record<string, any> | null;
}

export interface PerkRuleEvaluation {
  eligible: boolean;
  perk: any;
}

function asRecord(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {};
}

function asNumber(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function getNestedValue(source: Record<string, any>, path: string): any {
  return path.split('.').reduce((current, key) => {
    if (current == null || typeof current !== 'object') return undefined;
    return current[key];
  }, source as any);
}

export function buildPerkQuoteContext(input: PerkQuoteContext): Record<string, any> {
  const serviceConfiguration = asRecord(input.serviceConfiguration);
  const multiLocationQuote = asRecord(serviceConfiguration.multi_location_quote);
  const selectedTermMonths = asNumber(input.selectedTermMonths) || asNumber(multiLocationQuote.term_months) || 1;
  // Eligibility TCV is recurring service/quote MRC only. Prefer the guided quote's
  // computed MRC when present so NRC/setup/one-time add-ons never inflate tiers.
  const monthlyRecurringCents = asNumber(multiLocationQuote.total_mrc_cents)
    || asNumber(input.monthlyRecurringCents)
    || 0;

  return {
    selectedVariantKey: input.selectedVariantKey || null,
    selectedTermMonths,
    monthlyRecurringCents,
    serviceConfiguration,
    quote: {
      monthly_recurring_cents: monthlyRecurringCents,
      monthly_cents: monthlyRecurringCents,
      term_months: selectedTermMonths,
      tcv_mrc_cents: monthlyRecurringCents * selectedTermMonths,
      nrc_cents: asNumber(multiLocationQuote.total_nrc_cents) || 0,
      locations_count: asNumber(multiLocationQuote.configured_site_count) || 0,
      selected_circuit_count: asNumber(multiLocationQuote.selected_circuit_count) || 0,
      multi_location_quote: multiLocationQuote,
    },
  };
}

function evaluateCondition(condition: any, context: Record<string, any>): boolean {
  if (!condition || typeof condition !== 'object') return true;

  if (Array.isArray(condition.all)) return condition.all.every((entry: any) => evaluateCondition(entry, context));
  if (Array.isArray(condition.any)) return condition.any.some((entry: any) => evaluateCondition(entry, context));

  const field = typeof condition.field === 'string' ? condition.field : null;
  const actual = field ? getNestedValue(context, field) : undefined;
  const actualNumber = asNumber(actual);

  if ('eq' in condition && actual !== condition.eq) return false;
  if ('neq' in condition && actual === condition.neq) return false;
  if ('gte' in condition && (actualNumber == null || actualNumber < Number(condition.gte))) return false;
  if ('gt' in condition && (actualNumber == null || actualNumber <= Number(condition.gt))) return false;
  if ('lte' in condition && (actualNumber == null || actualNumber > Number(condition.lte))) return false;
  if ('lt' in condition && (actualNumber == null || actualNumber >= Number(condition.lt))) return false;
  if (Array.isArray(condition.in) && !condition.in.includes(actual)) return false;
  if (Array.isArray(condition.notIn) && condition.notIn.includes(actual)) return false;

  return true;
}

function interpolateTemplate(template: string, values: Record<string, any>): string {
  return template.replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => {
    const value = getNestedValue(values, key) ?? values[key];
    return value == null ? '' : String(value);
  }).replace(/\s+/g, ' ').trim();
}

function calculateRuleQuantity(valueRules: Record<string, any>, context: Record<string, any>): number | null {
  const field = typeof valueRules.quantity_field === 'string' ? valueRules.quantity_field : 'quote.monthly_recurring_cents';
  const sourceValue = asNumber(getNestedValue(context, field));
  const perCents = asNumber(valueRules.quantity_per_cents);
  if (sourceValue == null || !perCents || perCents <= 0) return null;

  const multiplier = asNumber(valueRules.quantity_multiplier) || 1;
  const offset = asNumber(valueRules.quantity_offset) || 0;
  const minimum = asNumber(valueRules.quantity_minimum) || 0;
  const maximum = asNumber(valueRules.quantity_maximum);
  let quantity = Math.floor(sourceValue / perCents) * multiplier + offset;
  if (minimum > 0) quantity = Math.max(quantity, minimum);
  if (maximum != null) quantity = Math.min(quantity, maximum);
  return quantity > 0 ? quantity : null;
}

function chooseValueTier(valueRules: Record<string, any>, context: Record<string, any>): Record<string, any> | null {
  const tiers = Array.isArray(valueRules.tiers) ? valueRules.tiers : [];
  let selected: Record<string, any> | null = null;

  for (const tier of tiers) {
    const when = tier?.when || tier?.condition;
    if (evaluateCondition(when, context)) selected = tier;
  }

  return selected;
}

export function evaluatePerkForQuote(perk: any, rawContext: PerkQuoteContext): PerkRuleEvaluation {
  const context = buildPerkQuoteContext(rawContext);
  const eligibilityRules = asRecord(
    perk?.eligibility_rules
      || perk?.metadata?.eligibility_rules
      || perk?.template_inputs?.eligibility_rules
      || perk?.fulfillment_requirements?.eligibility_rules,
  );
  const valueRules = asRecord(
    perk?.value_rules
      || perk?.metadata?.value_rules
      || perk?.template_inputs?.value_rules
      || perk?.fulfillment_requirements?.value_rules,
  );

  const legacyMinOrderValue = asNumber(perk?.min_order_value);
  if (legacyMinOrderValue && context.quote.tcv_mrc_cents < legacyMinOrderValue) {
    return { eligible: false, perk };
  }

  if (Object.keys(eligibilityRules).length > 0 && !evaluateCondition(eligibilityRules, context)) {
    return { eligible: false, perk };
  }

  const tier = Object.keys(valueRules).length > 0 ? chooseValueTier(valueRules, context) : null;
  if (Object.keys(valueRules).length > 0 && Array.isArray(valueRules.tiers) && !tier) {
    return { eligible: false, perk };
  }

  const effectiveQuantity = asNumber(tier?.quantity) || asNumber(valueRules.quantity) || calculateRuleQuantity(valueRules, context);
  const unitValueCents = asNumber(tier?.unit_value_cents) || asNumber(valueRules.unit_value_cents);
  const calculatedValueCents = effectiveQuantity && unitValueCents ? effectiveQuantity * unitValueCents : null;
  const effectiveValueCents = asNumber(tier?.value_cents) || asNumber(valueRules.value_cents) || calculatedValueCents || asNumber(perk?.savings_display_cents) || asNumber(perk?.rebate_amount) || null;
  const unitLabel = tier?.unit_label || valueRules.unit_label || 'perk';
  const templateValues = {
    ...context,
    quantity: effectiveQuantity,
    value_cents: effectiveValueCents,
    value_dollars: effectiveValueCents ? (effectiveValueCents / 100).toLocaleString('en-US') : null,
    unit_label: unitLabel,
  };

  const displayTemplate = tier?.display_template || valueRules.display_template || perk?.display_template;
  const effectiveDisplayTitle = typeof displayTemplate === 'string' && displayTemplate.trim()
    ? interpolateTemplate(displayTemplate, templateValues)
    : null;

  const effectivePerk = {
    ...perk,
    effective_quantity: effectiveQuantity,
    effective_value_cents: effectiveValueCents,
    effective_unit_label: unitLabel,
    effective_display_title: effectiveDisplayTitle,
    effective_rule_context: {
      monthly_recurring_cents: context.quote.monthly_recurring_cents,
      term_months: context.quote.term_months,
      tcv_mrc_cents: context.quote.tcv_mrc_cents,
      locations_count: context.quote.locations_count,
      selected_circuit_count: context.quote.selected_circuit_count,
    },
  };

  return { eligible: true, perk: effectivePerk };
}

export function filterPerksForQuote(perks: any[], context: PerkQuoteContext): any[] {
  return (perks || [])
    .map((perk) => evaluatePerkForQuote(perk, context))
    .filter((result) => result.eligible)
    .map((result) => result.perk);
}

export function buildEffectivePerkSnapshot(perk: any): Record<string, any> | null {
  if (!perk || perk.id === 'base') return null;

  return {
    perk_id: perk.id,
    perk_type: perk.perk_type || null,
    name: perk.effective_display_title || perk.render_title || perk.name || null,
    description: perk.render_description || perk.description || perk.perk_description || null,
    effective_quantity: perk.effective_quantity ?? null,
    effective_value_cents: perk.effective_value_cents ?? perk.savings_display_cents ?? perk.rebate_amount ?? null,
    effective_unit_label: perk.effective_unit_label ?? null,
    included_service_id: perk.included_service_id || perk.metadata?.included_service_id || perk.bundled_service_id || null,
    rule_context: perk.effective_rule_context || null,
  };
}
